import { Component, OnDestroy, OnInit } from '@angular/core';
import { EMPTY, Subject, forkJoin } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AlertService } from '../../../services/alert.service';
import { MyProfileService } from '../../../services/my-profile.service';
import {
  CollectorAvailabilityDay,
  CollectorAvailabilityDraftDay,
  CollectorEventBucket,
  DayState,
  Segment
} from '../availability.model';
import {
  MAX_SEGMENTS_PER_DAY,
  MAX_TOTAL_MINUTES,
  nextNDays
} from '../day-timeline.utils';

const DAYS_TO_PLAN = 7;

@Component({
  selector: 'app-collector-availability-planner',
  templateUrl: './collector-availability-planner.component.html',
  styleUrls: ['./collector-availability-planner.component.css']
})
export class CollectorAvailabilityPlannerComponent
  implements OnInit, OnDestroy
{
  days: DayState[] = [];
  events: CollectorEventBucket[] = [];
  loading = true;
  saving = false;

  readonly maxSegmentsPerDay = MAX_SEGMENTS_PER_DAY;
  readonly maxTotalHours = MAX_TOTAL_MINUTES / 60;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private myProfileService: MyProfileService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    const dates = nextNDays(DAYS_TO_PLAN);
    this.days = dates.map((date) => ({
      date,
      segments: [],
      dayOff: false,
      submitted: false
    }));

    const from = dates[0];
    const to = dates[dates.length - 1];

    forkJoin({
      buckets: this.myProfileService.getEventBuckets(from, to),
      availability: this.myProfileService.getAvailability(from, to)
    })
      .pipe(
        tap(({ buckets, availability }) => {
          this.events = buckets ?? [];
          this.applyExistingAvailability(availability ?? []);
        }),
        catchError((error) => {
          this.alertService.showError(
            error?.error?.message ?? 'Could not load availability'
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get submittedCount(): number {
    return this.days.filter((d) => d.submitted).length;
  }

  onDayChange(date: string, patch: Partial<DayState>): void {
    this.days = this.days.map((d) =>
      d.date === date ? { ...d, ...patch, submitted: false } : d
    );
  }

  onSubmitAll(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    const payload: CollectorAvailabilityDraftDay[] = this.days.map((d) => ({
      date: d.date,
      dayOff: d.dayOff,
      segments: d.segments.map((s) => ({
        startTime: s.startTime,
        endTime: s.endTime
      }))
    }));

    this.myProfileService
      .saveAvailability(payload)
      .pipe(
        tap((availability) => {
          this.applyExistingAvailability(availability ?? []);
          this.alertService.showInfo('Availability submitted');
        }),
        catchError((error) => {
          this.alertService.showError(
            error?.error?.message ?? 'Could not submit availability'
          );
          return EMPTY;
        }),
        finalize(() => {
          this.saving = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  trackByDate(_: number, day: DayState): string {
    return day.date;
  }

  private applyExistingAvailability(
    availability: CollectorAvailabilityDay[]
  ): void {
    const byDate = new Map(availability.map((d) => [d.date, d]));
    this.days = this.days.map((day) => {
      const stored = byDate.get(day.date);
      if (!stored) {
        return day;
      }
      const segments: Segment[] = (stored.segments ?? []).map((s, idx) => ({
        id: `${day.date}-${idx}`,
        startTime: s.startLocalMin,
        endTime: s.endLocalMin
      }));
      return {
        ...day,
        dayOff: !!stored.dayOff,
        segments,
        submitted: true
      };
    });
  }
}
